import React, { useState, useCallback } from "react";
import {
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  TableSortLabel,
  Box,
} from "@mui/material";
import { motion } from "framer-motion";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  DataTableColumn,
  DataTableRowControls,
  SortModel,
  FilterModel,
} from "../DataTable.types";
import {
  COLUMN_WIDTHS,
  TABLE_Z_INDEX,
  PINNED_SHADOWS,
  TABLE_BORDERS,
  defaultDataTableTheme,
  getDensityPaddingWithLeft,
  ROW_CONTROLS_LAYOUT,
} from "../DataTable.theme";
import { FilterRow } from "./FilterRow";
import { TableProgressRow } from "../TableProgressRow";
import { SortFilterPopover } from "./SortFilterPopover";

// The sort/filter controls are inline-flex, so the cell's own ellipsis never
// reaches their text: a narrow column would cut the label mid-word instead.
const headerLabelSx = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

export interface TableHeaderProps<TRow> {
  classes: any;
  themeStyles: any;
  densityStyles: any;
  selectable: boolean;
  selectionColumnWidth: number;
  rowControls?: DataTableRowControls<TRow>;
  rowControlsWidth: number;
  expandable: boolean;
  showExpandColumn: boolean;
  visibleColumns: DataTableColumn<TRow>[];
  columnWidths: Record<string, number>;
  isResizing: boolean;

  // Selection
  isIndeterminate: boolean;
  isAllSelected: boolean;
  onSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;

  // Sorting
  sortable: boolean;
  sortModel?: SortModel | null;
  onSortModelChange?: (model: SortModel | null) => void;

  // Resizing
  resizable: boolean;
  getColumnWidth: (
    column: DataTableColumn<TRow>,
  ) => number | string | undefined;
  getResizeHandleProps: (columnKey: string, currentWidth: number) => any;

  // Pinned columns
  pinnedLeft: DataTableColumn<TRow>[];
  pinnedRight: DataTableColumn<TRow>[];
  getPinnedLeftOffset: (index: number) => number;
  getPinnedRightOffset: (index: number) => number;
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  isScrolledLeft: boolean;
  isScrolledRight: boolean;

  // Filtering
  filterable: boolean;
  filterModel?: FilterModel;
  onFilterModelChange?: (model: FilterModel) => void;
  density: "compact" | "standard" | "comfortable";
  showColumnDividers: boolean;

  // Loading
  loading: boolean;
  totalColumnCount: number;
  disableCellLayoutAnimation: boolean;
}

const MotionTableCell = motion.create(TableCell);

const TableHeaderInternal = <TRow,>({
  classes,
  themeStyles,
  densityStyles,
  selectable,
  selectionColumnWidth,
  rowControls,
  rowControlsWidth,
  expandable,
  showExpandColumn,
  visibleColumns,
  columnWidths,
  isResizing,
  isIndeterminate,
  isAllSelected,
  onSelectAll,
  sortable,
  sortModel,
  onSortModelChange,
  resizable,
  getColumnWidth,
  getResizeHandleProps,
  pinnedLeft,
  pinnedRight,
  getPinnedLeftOffset,
  getPinnedRightOffset,
  lastPinnedLeftKey,
  firstPinnedRightKey,
  isScrolledLeft,
  isScrolledRight,
  filterable,
  filterModel,
  onFilterModelChange,
  density,
  showColumnDividers,
  loading,
  totalColumnCount,
  disableCellLayoutAnimation,
}: TableHeaderProps<TRow>) => {
  // ===== SORT FILTER POPOVER STATE =====
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverAnchorPosition, setPopoverAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [popoverColumnKey, setPopoverColumnKey] = useState<string | null>(null);
  const shouldAnimateCellLayout = !disableCellLayoutAnimation && !isResizing;
  const cellTransition = shouldAnimateCellLayout
    ? { duration: 0.2 }
    : { duration: 0 };
  const selectionColumnPadding = Math.max(
    0,
    selectionColumnWidth - COLUMN_WIDTHS.checkbox,
  );

  const handlePopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, columnKey: string) => {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      setPopoverAnchor(event.currentTarget);
      setPopoverAnchorPosition({
        top: rect.bottom,
        left: rect.left,
      });
      setPopoverColumnKey(columnKey);
    },
    [],
  );

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchor(null);
    setPopoverAnchorPosition(null);
    setPopoverColumnKey(null);
  }, []);

  // Check if column is sortable
  const isColumnSortable = (column: DataTableColumn<TRow>): boolean => {
    if (column.sortable !== undefined) return column.sortable;
    return sortable;
  };

  // Check if column has header filter popover
  const hasHeaderFilter = (column: DataTableColumn<TRow>): boolean => {
    return !!(
      (column.headerFilterOptions && column.headerFilterOptions.length > 0) ||
      column.headerFilterType === "select" ||
      column.headerFilterType === "search" ||
      column.headerFilterType === "custom"
    );
  };

  const getHeaderLabel = (column: DataTableColumn<TRow>): string =>
    typeof column.header === "string"
      ? column.header
      : column.headerText || column.key;

  // Handle sort click
  const handleSort = (column: DataTableColumn<TRow>) => {
    if (!onSortModelChange) return;

    const sortKey = column.sortKey || column.key;
    const currentDirection =
      sortModel?.field === sortKey ? sortModel.direction : null;

    if (currentDirection === null) {
      onSortModelChange({ field: sortKey, direction: "asc" });
    } else if (currentDirection === "asc") {
      onSortModelChange({ field: sortKey, direction: "desc" });
    } else {
      onSortModelChange({ field: sortKey, direction: "asc" });
    }
  };

  // Handle sort click from popover
  const handlePopoverSortChange = useCallback(
    (model: SortModel | null) => {
      if (onSortModelChange) {
        onSortModelChange(model);
      }
    },
    [onSortModelChange],
  );

  // Find the currently open popover column
  const popoverColumn = popoverColumnKey
    ? visibleColumns.find((c) => c.key === popoverColumnKey)
    : null;

  return (
    <TableHead className={classes.tableHead} sx={themeStyles.header}>
      <TableRow>
        {/* Selection checkbox in header */}
        {selectable && (
          <MotionTableCell
            layout={shouldAnimateCellLayout}
            transition={cellTransition}
            key="__select__"
            padding="none"
            className={classes.tableHeaderCell}
            style={{
              ...densityStyles,
              boxSizing: "border-box",
              width: selectionColumnWidth,
              minWidth: selectionColumnWidth,
              maxWidth: selectionColumnWidth,
              padding: `0 0 0 ${selectionColumnPadding}px`,
              overflow: "hidden",
              textOverflow: "clip",
              whiteSpace: "nowrap",
              borderBottom: filterable
                ? undefined
                : `${TABLE_BORDERS.emphasis} solid ${defaultDataTableTheme.borderColor}`,
              position:
                pinnedLeft.length > 0 || selectable ? "sticky" : undefined,
              left: 0,
              zIndex: TABLE_Z_INDEX.headerCell,
              backgroundColor: "inherit",
              ...(isScrolledLeft &&
                lastPinnedLeftKey === "__select__" && {
                  boxShadow: PINNED_SHADOWS.left,
                }),
            }}
          >
            <Checkbox
              indeterminate={isIndeterminate}
              checked={isAllSelected}
              onChange={onSelectAll}
              size="small"
              sx={{ padding: "4px" }}
            />
          </MotionTableCell>
        )}
        {/* Structural controls header: visually empty, accessible by label */}
        {rowControls && (
          <MotionTableCell
            layout={shouldAnimateCellLayout}
            transition={cellTransition}
            key="__row_controls__"
            padding="none"
            className={classes.tableHeaderCell}
            aria-label={rowControls.ariaLabel}
            style={{
              ...densityStyles,
              boxSizing: "border-box",
              width: rowControlsWidth,
              minWidth: rowControlsWidth,
              maxWidth: rowControlsWidth,
              padding: `0 ${ROW_CONTROLS_LAYOUT.paddingInline}px`,
              overflow: "hidden",
              textOverflow: "clip",
              whiteSpace: "nowrap",
              borderBottom: filterable
                ? undefined
                : `${TABLE_BORDERS.emphasis} solid ${defaultDataTableTheme.borderColor}`,
              position: "sticky",
              left: selectable ? selectionColumnWidth : 0,
              zIndex: TABLE_Z_INDEX.pinnedHeader,
              backgroundColor: "inherit",
              ...(isScrolledLeft &&
                lastPinnedLeftKey === "__row_controls__" && {
                  boxShadow: PINNED_SHADOWS.left,
                }),
            }}
          />
        )}
        {/* Expand column header */}
        {expandable && showExpandColumn && (
          <MotionTableCell
            layout={shouldAnimateCellLayout}
            transition={cellTransition}
            key="__expand__"
            padding="none"
            className={classes.tableHeaderCell}
            style={{
              ...densityStyles,
              boxSizing: "border-box",
              width:
                COLUMN_WIDTHS.checkbox +
                (!selectable && !rowControls
                  ? COLUMN_WIDTHS.firstColumnPadding
                  : 0),
              minWidth:
                COLUMN_WIDTHS.checkbox +
                (!selectable && !rowControls
                  ? COLUMN_WIDTHS.firstColumnPadding
                  : 0),
              maxWidth:
                COLUMN_WIDTHS.checkbox +
                (!selectable && !rowControls
                  ? COLUMN_WIDTHS.firstColumnPadding
                  : 0),
              padding:
                selectable || rowControls
                  ? 0
                  : `0 0 0 ${COLUMN_WIDTHS.firstColumnPadding}px`,
              overflow: "hidden",
              textOverflow: "clip",
              whiteSpace: "nowrap",
              borderBottom: filterable
                ? undefined
                : `${TABLE_BORDERS.emphasis} solid ${defaultDataTableTheme.borderColor}`,
              position: "sticky",
              left: (selectable ? selectionColumnWidth : 0) + rowControlsWidth,
              zIndex: TABLE_Z_INDEX.headerCell,
              backgroundColor: "inherit",
              ...(isScrolledLeft &&
                lastPinnedLeftKey === "__expand__" && {
                  boxShadow: PINNED_SHADOWS.left,
                }),
            }}
          />
        )}
        {visibleColumns.map((column, colIndex) => {
          const hasFirstColumnPadding =
            colIndex === 0 &&
            !selectable &&
            !rowControls &&
            (!expandable || !showExpandColumn);
          const isResizable =
            resizable && (column.resizable === undefined || column.resizable);
          const currentWidth = getColumnWidth(column);
          const numericWidth =
            typeof currentWidth === "number"
              ? (currentWidth as number) +
                (hasFirstColumnPadding ? COLUMN_WIDTHS.firstColumnPadding : 0)
              : COLUMN_WIDTHS.fallback;
          const firstColumnPadding = hasFirstColumnPadding
            ? COLUMN_WIDTHS.firstColumnPadding
            : 0;
          const minWidth =
            column.minWidth !== undefined
              ? column.minWidth + firstColumnPadding
              : undefined;
          const maxWidth =
            column.maxWidth !== undefined
              ? column.maxWidth + firstColumnPadding
              : undefined;

          return (
            <MotionTableCell
              layout={shouldAnimateCellLayout}
              transition={cellTransition}
              key={column.key}
              padding="none"
              className={`${classes.tableHeaderCell} ${column.headerClassName || ""} ${showColumnDividers && colIndex >= 0 ? classes.columnDivider : ""}`}
              align={column.headerAlign || column.align || "left"}
              style={{
                width: numericWidth,
                minWidth,
                maxWidth,
                position: "relative",
                borderBottom: filterable
                  ? undefined
                  : `${TABLE_BORDERS.emphasis} solid ${defaultDataTableTheme.borderColor}`,
                ...densityStyles,
                padding: hasFirstColumnPadding
                  ? getDensityPaddingWithLeft(
                      densityStyles.padding,
                      COLUMN_WIDTHS.firstColumnPadding,
                    )
                  : densityStyles.padding,
                ...(column.pinned === "left" && {
                  position: "sticky" as const,
                  left: getPinnedLeftOffset(pinnedLeft.indexOf(column)),
                  zIndex: TABLE_Z_INDEX.pinnedHeader,
                  backgroundColor: "inherit",
                  ...(isScrolledLeft &&
                    lastPinnedLeftKey === column.key && {
                      boxShadow: PINNED_SHADOWS.left,
                    }),
                }),
                ...(column.pinned === "right" && {
                  position: "sticky" as const,
                  right: getPinnedRightOffset(pinnedRight.indexOf(column)),
                  zIndex: TABLE_Z_INDEX.pinnedHeader,
                  backgroundColor: "inherit",
                  ...(isScrolledRight &&
                    firstPinnedRightKey === column.key && {
                      boxShadow: PINNED_SHADOWS.right,
                    }),
                }),
              }}
            >
              {isColumnSortable(column) && onSortModelChange ? (
                <TableSortLabel
                  active={
                    sortModel?.field === (column.sortKey || column.key) ||
                    (column.headerFilterValue &&
                      Array.isArray(column.headerFilterValue) &&
                      column.headerFilterValue.length > 0) ||
                    !!column.headerSearchValue
                  }
                  direction={
                    sortModel?.field === (column.sortKey || column.key)
                      ? sortModel.direction
                      : "asc"
                  }
                  IconComponent={
                    hasHeaderFilter(column)
                      ? FilterAltIcon
                      : KeyboardArrowUpIcon
                  }
                  onClick={(e) => {
                    if (hasHeaderFilter(column)) {
                      handlePopoverOpen(e, column.key);
                    } else {
                      handleSort(column);
                    }
                  }}
                  sx={{
                    maxWidth: "100%",
                    "& .MuiTableSortLabel-icon": {
                      opacity: 1,
                      transition: "all 0.2s ease-in-out",
                      ...(hasHeaderFilter(column) && {
                        transform: "none !important",
                      }),
                      color:
                        sortModel?.field === (column.sortKey || column.key) ||
                        (column.headerFilterValue &&
                          Array.isArray(column.headerFilterValue) &&
                          column.headerFilterValue.length > 0) ||
                        column.headerSearchValue
                          ? "var(--mela-color-primary)"
                          : "var(--mela-text-muted)",
                    },
                    "&:hover .MuiTableSortLabel-icon": {
                      transform: hasHeaderFilter(column)
                        ? "scale(1.1)"
                        : undefined,
                      color: "var(--mela-color-primary)",
                    },
                    "&.Mui-active .MuiTableSortLabel-icon": {
                      ...(hasHeaderFilter(column) && {
                        transform: "none !important",
                      }),
                      color:
                        sortModel?.field === (column.sortKey || column.key) ||
                        (column.headerFilterValue &&
                          Array.isArray(column.headerFilterValue) &&
                          column.headerFilterValue.length > 0) ||
                        column.headerSearchValue
                          ? "var(--mela-color-primary)"
                          : "var(--mela-text-muted)",
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={headerLabelSx}
                    title={
                      typeof column.header === "string"
                        ? column.header
                        : undefined
                    }
                  >
                    {column.header}
                  </Box>
                  {sortModel?.field === (column.sortKey || column.key) ? (
                    <Box
                      component="span"
                      sx={{
                        border: 0,
                        clip: "rect(0 0 0 0)",
                        height: "1px",
                        margin: -1,
                        overflow: "hidden",
                        padding: 0,
                        position: "absolute",
                        whiteSpace: "nowrap",
                        width: "1px",
                      }}
                    >
                      {sortModel.direction === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </TableSortLabel>
              ) : hasHeaderFilter(column) ? (
                <Box
                  component="span"
                  onClick={(e) => handlePopoverOpen(e, column.key)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    maxWidth: "100%",
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover .filter-icon": {
                      color: "var(--mela-color-primary)",
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={headerLabelSx}
                    title={
                      typeof column.header === "string"
                        ? column.header
                        : undefined
                    }
                  >
                    {column.header}
                  </Box>
                  <FilterAltIcon
                    className="filter-icon"
                    sx={{
                      fontSize: "1rem",
                      transition: "all 0.2s ease-in-out",
                      color:
                        (column.headerFilterValue &&
                          Array.isArray(column.headerFilterValue) &&
                          column.headerFilterValue.length > 0) ||
                        column.headerSearchValue
                          ? "var(--mela-color-primary)"
                          : "var(--mela-text-muted)",
                    }}
                  />
                </Box>
              ) : (
                column.header
              )}
              {/* Resize handle */}
              {isResizable && (
                <Box {...getResizeHandleProps(column.key, numericWidth)} />
              )}
            </MotionTableCell>
          );
        })}
      </TableRow>
      {/* ===== FILTER ROW ===== */}
      {filterable && filterModel && onFilterModelChange && (
        <TableRow>
          <FilterRow
            columns={visibleColumns}
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            density={density}
            hasSelectable={selectable}
            selectionColumnWidth={selectionColumnWidth}
            hasRowControls={!!rowControls}
            rowControlsWidth={rowControlsWidth}
            hasExpandable={expandable && showExpandColumn}
            getColumnWidth={getColumnWidth}
            pinnedLeft={pinnedLeft}
            pinnedRight={pinnedRight}
            getPinnedLeftOffset={getPinnedLeftOffset}
            getPinnedRightOffset={getPinnedRightOffset}
            showColumnDividers={showColumnDividers}
            isScrolledLeft={isScrolledLeft}
            isScrolledRight={isScrolledRight}
            isResizing={isResizing}
            lastPinnedLeftKey={lastPinnedLeftKey}
            firstPinnedRightKey={firstPinnedRightKey}
          />
        </TableRow>
      )}
      <TableProgressRow colSpan={totalColumnCount} loading={loading} />

      {/* ===== SORT FILTER POPOVER ===== */}
      {popoverColumn && hasHeaderFilter(popoverColumn) && (
        <SortFilterPopover
          anchorEl={popoverAnchor}
          anchorPosition={popoverAnchorPosition}
          open={!!popoverAnchor}
          onClose={handlePopoverClose}
          sortKey={popoverColumn.sortKey || popoverColumn.key}
          headerLabel={getHeaderLabel(popoverColumn)}
          sortModel={sortModel}
          onSortChange={handlePopoverSortChange}
          type={popoverColumn.headerFilterType}
          renderCustomContent={popoverColumn.renderHeaderFilterContent}
          searchValue={popoverColumn.headerSearchValue}
          searchPlaceholder={popoverColumn.headerSearchPlaceholder}
          onSearchChange={popoverColumn.onHeaderSearchChange}
          filterOptions={popoverColumn.headerFilterOptions || []}
          renderFilterOption={popoverColumn.renderHeaderFilterOption}
          selectedValues={popoverColumn.headerFilterValue || []}
          hideSort={popoverColumn.headerFilterHideSort}
          showSearch={popoverColumn.headerFilterShowSearch}
          searchMode={popoverColumn.headerFilterSearchMode}
          loading={popoverColumn.headerFilterLoading}
          showSelectAll={popoverColumn.headerFilterShowSelectAll}
          multiple={popoverColumn.headerFilterMultiple}
          onApply={(selected, sort) => {
            if (popoverColumn.onHeaderFilterApply) {
              popoverColumn.onHeaderFilterApply(selected, sort);
            }
          }}
        />
      )}
    </TableHead>
  );
};

export const TableHeader = React.memo(TableHeaderInternal) as <TRow>(
  props: TableHeaderProps<TRow>,
) => React.ReactElement;
